import { Link } from 'react-router-dom';
import { events } from '@/data/mock';
import usePageTitle from '@/hooks/usePageTitle';
import { Calendar, MapPin } from 'lucide-react';

const EventsPage = () => {
  usePageTitle('Events');
  const upcomingEvents = events.filter(e => e.year >= 2026);
  const pastEvents = events.filter(e => e.year < 2026);

  return (
    <div data-testid="events-page">
      {/* Hero */}
      <section className="bg-brand-dark py-20 lg:py-24">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="text-3xl lg:text-4xl font-semibold text-white mb-6">
              Events
            </h1>
            <p className="text-lg text-gray-400 mb-8">
              From community resets to camping retreats, there is always a reason to come together.
            </p>
            <Link
              to="/calendar"
              className="inline-block bg-brand-gold px-6 py-3 text-sm font-semibold text-brand-dark hover:opacity-90"
              data-testid="view-calendar-btn"
            >
              View Full Calendar
            </Link>
          </div>
        </div>
      </section>

      {/* Upcoming Events */}
      <section className="bg-white py-16 lg:py-20">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="flex items-center gap-3 mb-8">
              <div className="w-2 h-8 bg-brand-gold"></div>
              <h2 className="text-2xl font-semibold text-brand-dark" data-testid="upcoming-events-heading">
                Upcoming Events
              </h2>
            </div>

            {upcomingEvents.length === 0 ? (
              <p className="text-gray-500 mb-8">No upcoming events scheduled. Check back soon or view our calendar.</p>
            ) : (
              <div className="space-y-6 mb-8">
                {upcomingEvents.map((event) => (
                  <article
                    key={event.id}
                    className="bg-gray-50 border-l-4 border-brand-gold p-6"
                    data-testid={`upcoming-event-${event.id}`}
                  >
                    <h3 className="text-lg font-semibold text-brand-dark mb-2">
                      {event.title}
                    </h3>
                    <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600 mb-3">
                      <span className="flex items-center gap-1.5">
                        <Calendar className="w-4 h-4 text-brand-gold" />
                        {event.date}
                      </span>
                      <span className="flex items-center gap-1.5">
                        <MapPin className="w-4 h-4 text-brand-gold" />
                        {event.location}
                      </span>
                    </div>
                    {event.title.includes('Elevated Escape') && (
                      <Link
                        to="/elevated-escape/tickets"
                        className="inline-block text-sm font-medium text-brand-gold hover:underline"
                      >
                        Learn More & Get Tickets →
                      </Link>
                    )}
                  </article>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Divider */}
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <div className="max-w-3xl">
          <hr className="border-gray-200" />
        </div>
      </div>

      {/* Past Events */}
      <section className="bg-white py-16 lg:py-20">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="flex items-center gap-3 mb-8">
              <div className="w-2 h-8 bg-gray-300"></div>
              <h2 className="text-2xl font-semibold text-gray-500" data-testid="past-events-heading">
                Past Events
              </h2>
            </div>

            {pastEvents.length === 0 ? (
              <p className="text-gray-500">No past events to display.</p>
            ) : (
              <div className="space-y-4">
                {pastEvents.map((event) => (
                  <article
                    key={event.id}
                    className="border-b border-gray-100 pb-4 last:border-0"
                    data-testid={`past-event-${event.id}`}
                  >
                    <h3 className="text-base font-medium text-gray-600 mb-1">
                      {event.title}
                    </h3>
                    <p className="text-sm text-gray-400">
                      {event.date} · {event.location}
                    </p>
                  </article>
                ))}
              </div>
            )}

            <div className="mt-16 pt-8 border-t border-gray-200 text-center">
              <p className="text-gray-600 mb-2">Want to partner or sponsor an event?</p>
              <a
                href="mailto:admin@elevatingmn.com"
                className="text-brand-gold font-medium hover:underline"
                data-testid="events-contact-link"
              >
                admin@elevatingmn.com
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default EventsPage;
